import React, { useState, useMemo, useEffect } from "react";
import { Box } from "@mui/material";
import Grid from "@mui/material/Grid";
import dayjs, { Dayjs } from "dayjs";
import FilterGrid from "../../../components/Charts/FilterGrid";
import MetricStats from "../../../components/Charts/RevenueStatsNew";
import ClientStats from "../../../components/Charts/ClientStats";
import LeadStats from "../../../components/Charts/LeadStats";
import FastAPIClient from "../../../client";
import config from "../../../config";

const client = new FastAPIClient(config);

interface User {
    user_id: string;
    first_name: string;
    last_name: string;
    team_name?: string | null;
    warehouse_name?: string | null;
}

type MonthlyStat = {
    user_id: string;
    first_name: string;
    last_name: string;
    warehouse_name: string;
    year: number;
    month: number;
    monthly_revenue: number;
    monthly_volume: number;
    monthly_margin: number;
    distinct_clients: number;
    team_name: string;
    product_level_1?: string;
    client_level_1?: string;
};

const Dashboard: React.FC = () => {
    const [selectedUser, setSelectedUser] = useState<string>("all");
    const [selectedWarehouse, setSelectedWarehouse] = useState<string>("all");
    const [data, setData] = useState<User[]>([]);
    const [transactionData, setTransactionData] = useState<MonthlyStat[]>([]);
    const [beginDate, setBeginDate] = useState<string>();
    const [finalDate, setFinalDate] = useState<string>();
    const [dateRange, setDateRange] = useState<[Dayjs | null, Dayjs | null]>([
        dayjs().startOf("year"),
        dayjs(),
    ]);

    useEffect(() => {
        fetchActivity();
    }, []);

    const fetchActivity = async () => {
        try {
            const response = await client.fetchTeamMembers();
            setData(response.results);
        } catch (error) {
            console.error("Error fetching activity:", error);
        }
    };

    useEffect(() => {
        const [startDate, endDate] = dateRange;

        if (startDate && endDate) {
            const formattedStartDate = startDate.format("YYYY-MM-DD");
            const formattedEndDate = endDate.format("YYYY-MM-DD");

            setTransactionData([]);
            fetchMonthlyStats(formattedStartDate, formattedEndDate);
        }
    }, [dateRange]);

    const fetchMonthlyStats = async (startDate: string, endDate: string) => {
        try {
            const response = await client.fetchMonthlyStats({
                startDate,
                endDate,
            });
            setTransactionData(response.results);
        } catch (error) {
            console.error("Error fetching monthly stats:", error);
        }
    };

    const filteredUsers =
        selectedWarehouse === "all"
            ? data
            : data.filter((user) => user.warehouse_name === selectedWarehouse);

    const uniqueUsers = useMemo(() => {
        return filteredUsers.map((user) => ({
            user_id: user.user_id,
            first_name: user.first_name,
            last_name: user.last_name,
            warehouse_name: user.warehouse_name,
        }));
    }, [filteredUsers]);

    const uniqueWarehouses = useMemo(() => {
        const warehouses = data
            .map((user) => user.warehouse_name)
            .filter(Boolean);
        return Array.from(new Set(warehouses));
    }, [data]);

    const handleUserSelectChange = (value: string) => {
        setSelectedUser(value);

        if (value !== "all") {
            const selectedUserData = data.find(
                (user) => `${user.first_name} ${user.last_name}` === value
            );
            if (selectedUserData) {
                setSelectedWarehouse(selectedUserData.warehouse_name || "all");
            }
        } else {
            setSelectedWarehouse("all");
        }
    };

    const handleWarehouseSelectChange = (value: string) => {
        setSelectedWarehouse(value);

        if (value !== "all") {
            data.find((user) => user.warehouse_name === value);
        } else {
            setSelectedUser("all");
        }
    };

    useEffect(() => {
        const [startDate, endDate] = dateRange;

        setBeginDate(startDate.format("YYYY-MM-DD"));
        setFinalDate(endDate.format("YYYY-MM-DD"));
    }, [dateRange]);

    return (
        <div className="w-full items-center flex flex-col justify-center">
            <div className="w-full">
                <FilterGrid
                    selectedUser={selectedUser}
                    selectedWarehouse={selectedWarehouse}
                    dateRange={dateRange}
                    uniqueUsers={uniqueUsers}
                    uniqueWarehouses={uniqueWarehouses}
                    onUserChange={handleUserSelectChange}
                    onWarehouseChange={handleWarehouseSelectChange}
                    onDateRangeChange={setDateRange}
                />

                <Grid container spacing={1} columns={{ xs: 1, md: 5 }}>
                    {/* First column group: spans 3 columns */}
                    <Grid size={3}>
                        <Box
                            sx={{
                                border: "1px solid #ddd", // light gray border
                                borderRadius: 2,
                                overflow: "hidden", // clips child borders
                            }}
                        >
                            <Grid
                                container
                                spacing={0}
                                columns={{ xs: 1, md: 3 }}
                            >
                                {[
                                    // map to avoid repetition
                                    {
                                        key: "monthly_volume",
                                        title: "Volume",
                                        unit: "hL",
                                    },
                                    {
                                        key: "monthly_revenue",
                                        title: "Chiffre d'affaires",
                                        unit: "€",
                                    },
                                    {
                                        key: "monthly_margin",
                                        title: "Marge",
                                        unit: "€",
                                    },
                                ].map((metric, index) => (
                                    <Grid
                                        key={metric.key}
                                        size={1}
                                        sx={{
                                            borderRight:
                                                index !== 2
                                                    ? "1px solid #ddd"
                                                    : "none", // vertical line except last
                                            padding: 0,
                                        }}
                                    >
                                        <MetricStats
                                            selectedUser={selectedUser}
                                            selectedWarehouse={
                                                selectedWarehouse
                                            }
                                            metricKey={
                                                metric.key as
                                                    | "monthly_volume"
                                                    | "monthly_revenue"
                                                    | "monthly_margin"
                                            }
                                            unit={metric.unit}
                                            title={metric.title}
                                            data={transactionData}
                                        />
                                    </Grid>
                                ))}
                            </Grid>
                        </Box>
                    </Grid>

                    {/* Second column group: spans 2 columns */}
                    <Grid size={2}>
                        <Grid container spacing={1} columns={{ xs: 1, md: 2 }}>
                            <Grid size={1}>
                                <Box
                                    sx={{
                                        border: "1px solid #ddd", // light gray border
                                        borderRadius: 2,
                                        overflow: "hidden", // clips child borders
                                    }}
                                >
                                    <ClientStats
                                        selectedUser={selectedUser}
                                        selectedWarehouse={selectedWarehouse}
                                        metricKey={"monthly_volume"}
                                        title={"Clients actifs"}
                                        data={transactionData}
                                        startDate={beginDate}
                                        endDate={finalDate}
                                    />
                                </Box>
                            </Grid>
                            <Grid size={1}>
                                <Box
                                    sx={{
                                        border: "1px solid #ddd", // light gray border
                                        borderRadius: 2,
                                        overflow: "hidden", // clips child borders
                                    }}
                                >
                                    <LeadStats
                                        selectedUser={selectedUser}
                                        selectedWarehouse={selectedWarehouse}
                                        metricKey={"monthly_revenue"}
                                        title={"Prospects"}
                                        data={transactionData}
                                    />
                                </Box>
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
            </div>
        </div>
    );
};

export default Dashboard;
